<template>
  <div class="bg-white p-6 rounded-lg shadow-md mb-6">
    <h2 class="text-xl font-semibold mb-4 text-[#00225E]">Invoice Metrics</h2>
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
      <div v-if="canSeePaidMetrics" class="bg-green-50 p-4 rounded-lg">
        <h3 class="text-sm font-medium text-green-800">Amount Paid</h3>
        <p class="text-2xl font-bold text-green-600">${{ formatCurrency(metrics.amountPaid) }}</p>
        <p class="text-xs text-green-600">{{ metrics.invoicesPaid }} invoices</p>
      </div>
      
      <!-- Amount Due - Hidden visually -->
      <div v-if="false" class="bg-yellow-50 p-4 rounded-lg">
        <h3 class="text-sm font-medium text-yellow-800">Amount Due</h3>
        <p class="text-2xl font-bold text-yellow-600">${{ formatCurrency(metrics.amountDue) }}</p>
        <p class="text-xs text-yellow-600">{{ metrics.invoicesDue }} invoices</p>
      </div>

      <div class="bg-blue-50 p-4 rounded-lg">
        <h3 class="text-sm font-medium text-blue-800">Invoices Approved</h3>
        <p class="text-2xl font-bold text-blue-600">{{ metrics.invoicesSuggested }}</p>
        <p class="text-xs text-blue-600">${{ formatCurrency(metrics.suggestedAmount) }}</p>
      </div>

      <div v-if="canSeePaidMetrics" class="bg-purple-50 p-4 rounded-lg">
        <h3 class="text-sm font-medium text-purple-800">In Acceptance</h3>
        <p class="text-2xl font-bold text-purple-600">{{ metrics.invoicesInMicuAcceptance }}</p>
        <p class="text-xs text-purple-600">${{ formatCurrency(metrics.inMicuAcceptanceAmount) }}</p>
      </div>

      <div class="bg-orange-50 p-4 rounded-lg">
        <h3 class="text-sm font-medium text-orange-800">Invoices Return</h3>
        <p class="text-2xl font-bold text-orange-600">{{ metrics.invoicesReturn }}</p>
        <p class="text-xs text-orange-600">${{ formatCurrency(metrics.returnAmount) }}</p>
      </div>

      <div class="bg-blue-50 p-4 rounded-lg">
        <h3 class="text-sm font-medium text-blue-800">Reject/Dispute</h3>
        <p class="text-2xl font-bold text-blue-600">{{ metrics.invoicesDeclined }}</p>
        <p class="text-xs text-blue-600">${{ formatCurrency(metrics.declinedAmount) }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import { computed } from 'vue';

export default {
  props: {
    approvedWOData: {
      type: Array,
      default: () => []
    },
    submittedInvoicesData: {
      type: Array,
      default: () => []
    },
    paidInvoicesData: {
      type: Array,
      default: () => []
    },
    returnInvoicesData: {
      type: Array,
      default: () => []
    },
    userRole: {
      type: String,
      default: 'user'
    }
  },
  setup(props) {
    // Regular 'user' role must not see Paid / In Acceptance metrics
    const canSeePaidMetrics = computed(() => props.userRole !== 'user');

    const metrics = computed(() => {
      const approvedWO = props.approvedWOData || [];
      const submittedInvoices = props.submittedInvoicesData || [];
      const paidInvoices = props.paidInvoicesData || [];
      const returnInvoices = props.returnInvoicesData || [];

      const amountPaid = paidInvoices.reduce((sum, invoice) => sum + (invoice.amount || 0), 0);
      const invoicesPaid = paidInvoices.length;

      const returnAmount = returnInvoices.reduce((sum, invoice) => sum + (invoice.amount || 0), 0);
      const invoicesReturn = returnInvoices.length;

      const pastDueInvoices = submittedInvoices.filter(inv => 
        inv.dueStatus && inv.dueStatus.toLowerCase().includes('past due')
      );
      const amountDue = pastDueInvoices.reduce((sum, invoice) => sum + (invoice.amount || 0), 0);
      const invoicesDue = pastDueInvoices.length;

      const suggestedInvoices = submittedInvoices.filter(inv => 
        inv.status && inv.status.toLowerCase().includes('approved')
      );
      
      const invoicesSuggested = suggestedInvoices.length;
      const suggestedAmount = suggestedInvoices.reduce((sum, invoice) => sum + (invoice.amount || 0), 0);

      const inMicuAcceptanceInvoices = submittedInvoices.filter(inv => 
        inv.status && inv.status.toLowerCase().includes('in acceptance')
      );
      
      const invoicesInMicuAcceptance = inMicuAcceptanceInvoices.length;
      const inMicuAcceptanceAmount = inMicuAcceptanceInvoices.reduce((sum, invoice) => sum + (invoice.amount || 0), 0);
      
      const declinedInvoices = submittedInvoices.filter(inv => 
        inv.status && inv.status.toLowerCase().includes('reject')
      );
      
      const invoicesDeclined = declinedInvoices.length;
      const declinedAmount = declinedInvoices.reduce((sum, invoice) => sum + (invoice.amount || 0), 0);
      
      const totalSubmitted = submittedInvoices.length;
      const suggestedPercentage = totalSubmitted > 0 ? Math.round((invoicesSuggested / totalSubmitted) * 100) : 0;
      const inMicuAcceptancePercentage = totalSubmitted > 0 ? Math.round((invoicesInMicuAcceptance / totalSubmitted) * 100) : 0;
      const declinedPercentage = totalSubmitted > 0 ? Math.round((invoicesDeclined / totalSubmitted) * 100) : 0;

      return {
        amountPaid,
        invoicesPaid,
        returnAmount,
        invoicesReturn,
        amountDue,
        invoicesDue,
        invoicesSuggested,
        suggestedAmount,
        invoicesInMicuAcceptance,
        inMicuAcceptanceAmount,
        invoicesDeclined,
        declinedAmount,
        suggestedPercentage,
        inMicuAcceptancePercentage,
        declinedPercentage
      };
    });

    const formatCurrency = (amount) => {
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount);
    };

    return {
      metrics,
      formatCurrency,
      canSeePaidMetrics
    };
  }
};
</script>
